import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { invoiceApi, invoiceItemDtlApi, productApi, productPriceApi, productCategoryApi, taxConfigApi, inventoryItemDtlApi, inventorySummaryApi } from "../api/api";
import { parsePurityFactor } from "../utils/purityUtils";
import { emitDataMutation } from "../utils/appEvents";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";
import SearchableDropdown from "../components/SearchableDropdown";
import PrintInvoiceModal from "../components/PrintInvoiceModal";
import RvDataCard, { RvDataCardList } from "../components/RvDataCard";
import LineItemsEditor from "../components/LineItemsEditor";

/* ── Status config ── */
const STATUS_CONFIG = {
  Draft: { color: "draft", label: "Draft" },
  Finalised: { color: "finalised", label: "Finalised" },
  Cancelled: { color: "cancelled", label: "Cancelled" },
};

/* ── Toast ── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ title, message, confirmLabel, confirmClass, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop" style={{ zIndex: 500 }}>
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><AlertIcon /></div>
        <h3 className="rv-dialog-title">{title}</h3>
        <p className="rv-dialog-desc">{message}</p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Go Back</button>
          <button className={`rv-btn ${confirmClass}`} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Invoice Form Modal ── */
/* ── Invoice Form Modal ── */
function InvoiceModal({ initial, products, allItems, inventoryItems = [], inventorySummaries = [], prices, categories, taxConfigs, onSave, onClose, saving }) {
  const isEdit = !!initial?.invoiceID;
  const isLocked = initial?.invoiceStatus === "Finalised" || initial?.invoiceStatus === "Cancelled";
  const todayISO = new Date().toISOString().split("T")[0];

  /* ── Lookup maps (built once) ── */
  // productID → available stock quantity from inventory summary
  const stockMap = useMemo(() => {
    const map = new Map();
    (inventorySummaries || []).forEach(s => {
      const pid = Number(s.productID ?? s.productId);
      if (pid) map.set(pid, Number(s.quantity) || 0);
    });
    return map;
  }, [inventorySummaries]);

  // Helper to get real available stock in vault
  const getProductAvailableStock = useCallback((productId) => {
    const pid = Number(productId);
    if (!pid) return 0;
    if (stockMap.has(pid)) {
      return stockMap.get(pid);
    }
    // Fallback: count active physical pieces in inventory items
    return (inventoryItems || []).filter(
      i => Number(i.productID) === pid && (Number(i.quantity) || 0) > 0
    ).length;
  }, [stockMap, inventoryItems]);

  /* ── Lookup maps (built once) ── */
  // productID → latest price record
  const latestPriceMap = useMemo(() => {
    const map = new Map();
    prices.forEach(p => {
      const existing = map.get(p.productID);
      if (p.effdt && p.effdt.slice(0, 10) <= todayISO && (!existing || new Date(p.effdt) > new Date(existing.effdt))) {
        map.set(p.productID, p);
      }
    });
    return map;
  }, [prices, todayISO]);

  // Add this alongside the other useMemo maps
  const categoryMap = useMemo(() =>
    new Map(categories.map(c => [Number(c.categoryID), c])),
    [categories]);

  // inventoryItemID → inventoryItem object
  const inventoryItemMap = useMemo(() =>
    new Map((inventoryItems || []).map(i => [Number(i.inventoryItemID), i])),
    [inventoryItems]);

  // taxConfigID → latest taxPercentage
  const taxRateMap = useMemo(() => {
    const map = new Map();
    taxConfigs.forEach(t => {
      const existing = map.get(t.taxConfigID);
      if (t.effdt && t.effdt.slice(0, 10) <= todayISO && (!existing || new Date(t.effdt) > new Date(existing.effdt))) {
        map.set(t.taxConfigID, t);
      }
    });
    return map;
  }, [taxConfigs, todayISO]);

  // Default active tax in system (fallback when category has taxConfigID as null in DB)
  const defaultActiveTax = useMemo(() => {
    if (!taxConfigs || taxConfigs.length === 0) return null;
    const currentActive = taxConfigs.filter(t =>
      t.isActive !== false && (!t.effdt || t.effdt.slice(0, 10) <= todayISO)
    );
    const list = currentActive.length > 0
      ? currentActive
      : taxConfigs.filter(t => !t.effdt || t.effdt.slice(0, 10) <= todayISO);
    const candidates = list.length > 0 ? list : taxConfigs;
    const sorted = [...candidates].sort((a, b) => new Date(b.effdt || 0) - new Date(a.effdt || 0));
    const gst = sorted.find(t => String(t.taxName || "").toUpperCase().includes("GST"));
    return gst || sorted[0] || null;
  }, [taxConfigs, todayISO]);

  // Helper to resolve tax configuration from category, parent category, or active tax fallback
  const resolveCategoryTax = useCallback((categoryID) => {
    let cat = categoryMap.get(Number(categoryID));
    while (cat) {
      if (cat.taxConfigID && Number(cat.taxConfigID) > 0) {
        const tax = taxRateMap.get(Number(cat.taxConfigID));
        if (tax) return { taxConfigID: Number(cat.taxConfigID), taxConfig: tax, categoryName: cat.categoryName, isFallback: false };
      }
      if (!cat.parentCategoryID || Number(cat.parentCategoryID) === 0) break;
      cat = categoryMap.get(Number(cat.parentCategoryID));
    }
    // Fallback: active store tax so categories without taxConfigID still auto-fetch the store tax
    if (defaultActiveTax) {
      return { taxConfigID: Number(defaultActiveTax.taxConfigID) || 0, taxConfig: defaultActiveTax, categoryName: "", isFallback: true };
    }
    return { taxConfigID: 0, taxConfig: null, categoryName: "", isFallback: false };
  }, [categoryMap, taxRateMap, defaultActiveTax]);

  // productID → product object
  const productMap = useMemo(() =>
    new Map(products.map(p => [Number(p.productID), p])),
    [products]);

  /* ── Helper: resolve auto-fill values for a productID ── */
  const resolveProductDefaults = useCallback((productID) => {
    const pid = Number(productID);
    const product = productMap.get(pid);
    if (!product) return { unitPrice: "", taxConfigID: 0, taxPercentage: "", pricingType: "Fixed", purityStandard: "" };

    const price = latestPriceMap.get(pid);
    const resolvedTax = resolveCategoryTax(product.categoryID);

    return {
      unitPrice: price?.grossSellingPrice != null && Number(price.grossSellingPrice) > 0
        ? String(price.grossSellingPrice)
        : "",
      taxConfigID: resolvedTax.taxConfigID || 0,
      taxPercentage: resolvedTax.taxConfig?.taxPercentage != null
        ? String(resolvedTax.taxConfig.taxPercentage)
        : "",
      pricingType: product.pricingType || "Fixed",
      purityStandard: product.purityStandard || "",
    };
  }, [productMap, latestPriceMap, resolveCategoryTax]);

  /* ── Form state ── */
  const [form, setForm] = useState({
    invoiceNumber: initial?.invoiceNumber ?? "",
    invoiceDate: initial?.invoiceDate ? initial.invoiceDate.split("T")[0] : todayISO,
    customerName: initial?.customerName ?? "",
    customerPhone: initial?.customerPhone ?? "",
    customerAddress: initial?.customerAddress ?? "",
    customerGSTIN: initial?.customerGSTIN ?? "",
  });

  const blankLine = () => ({
    invoiceItemID: 0,
    productID: "",
    inventoryItemID: "",
    quantity: "",
    billedWeight: "",
    unitPrice: "",
    metalRate: "",
    makingCharges: "",
    pricingType: "Fixed",
    purityStandard: "",
    taxConfigID: 0,
    taxPercentage: "",
    lineAmount: 0,
    taxAmount: 0,
    totalPrice: 0,
  });

  const [lines, setLines] = useState(() => {
    const validProductIds = new Set((products || []).map(p => Number(p.productID)));
    const rawLines = initial?.lines?.length ? initial.lines : [];
    const validLines = rawLines.filter(l => !l.productID || validProductIds.has(Number(l.productID)));
    return validLines
      .slice()
      .sort((a, b) => Number(a.invoiceItemDtlID || a.invoiceItemID || a.lineNumber || 0) - Number(b.invoiceItemDtlID || b.invoiceItemID || b.lineNumber || 0))
      .map(l => {
        const prod = l.productID ? productMap.get(Number(l.productID)) : null;
        const pricingType = l.pricingType || prod?.pricingType || "Fixed";
        const purityStd = l.purityStandard || prod?.purityStandard || "";
        const isWeightBased = ["per gram", "per carat"].includes(
          pricingType.toLowerCase()
        );
        const purity = parsePurityFactor(purityStd);
        const derivedMetalRate = isWeightBased && purity > 0 && Number(l.unitPrice) > 0
          ? (Number(l.unitPrice) / purity).toFixed(2)
          : "";

        return {
          ...blankLine(),
          ...l,
          productID: l.productID != null && l.productID !== "" ? Number(l.productID) : "",
          inventoryItemID: l.inventoryItemID != null ? String(l.inventoryItemID) : "",
          pricingType,
          purityStandard: purityStd,
          metalRate: l.metalRate || derivedMetalRate,
          makingCharges: l.makingCharges != null ? String(l.makingCharges) : "",
        };
      });
  });

  const [itemMeta, setItemMeta] = useState({});
  const [errors, setErrors] = useState({});

  /* ── Single Item Entry Form State ── */
  const blankItemForm = () => ({
    invoiceItemID: 0,
    productID: "",
    inventoryItemID: "",
    quantity: 1,
    billedWeight: "",
    unitPrice: "",
    metalRate: "",
    makingCharges: "",
    pricingType: "Fixed",
    purityStandard: "",
    taxConfigID: 0,
    taxPercentage: "",
    lineAmount: 0,
    taxAmount: 0,
    totalPrice: 0,
  });

  const [itemForm, setItemForm] = useState(blankItemForm);
  const [editingIndex, setEditingIndex] = useState(null);
  const [itemFormErrors, setItemFormErrors] = useState({});
  const [formItemMeta, setFormItemMeta] = useState(null);

  // Auto-resolve existing lines with inventoryItemID on modal load
  useEffect(() => {
    lines.forEach((line, idx) => {
      if (line.inventoryItemID) {
        const id = Number(line.inventoryItemID);
        if (id > 0) {
          if (inventoryItemMap.has(id)) {
            setItemMeta(prev => ({ ...prev, [idx]: inventoryItemMap.get(id) }));
          } else {
            inventoryItemDtlApi.getById(id)
              .then(res => {
                const item = res?.data ?? res;
                if (item && item.inventoryItemID) {
                  setItemMeta(prev => ({ ...prev, [idx]: item }));
                }
              })
              .catch(() => { });
          }
        }
      }
    });
  }, [inventoryItemMap]);

  /* ── Auto-calc amounts for one line ── */
  const calcLine = useCallback((line) => {
    const qty = Number(line.quantity) || 0;
    const unitPrice = Number(line.unitPrice) || 0;
    const taxPct = Number(line.taxPercentage) || 0;
    const billedWt = Number(line.billedWeight) || 0;
    const making = Number(line.makingCharges) || 0;

    // Pricing-type-aware formula:
    // Per Gram / Per Carat → baseAmount = billedWeight × unitPrice (weight drives cost)
    // Fixed / Per Piece / anything else → baseAmount = quantity × unitPrice
    // billedWeight still appears on the invoice for GST/assay purposes but does NOT
    // affect the line total unless the product is actually weight-based.
    const isWeightBased = ["per gram", "per carat"].includes(
      (line.pricingType || "").toLowerCase()
    );
    const baseAmount = isWeightBased && billedWt > 0
      ? billedWt * unitPrice
      : qty * unitPrice;

    const lineAmount = Math.round((baseAmount + making) * 100) / 100;
    const taxAmount = Math.round(lineAmount * taxPct / 100 * 100) / 100;
    const totalPrice = Math.round((lineAmount + taxAmount) * 100) / 100;

    return { ...line, lineAmount, taxAmount, totalPrice };
  }, []);

  /* ── Form Field Handlers for Item Entry Form ── */
  const handleItemFormProductChange = (val) => {
    const pid = val != null && val !== "" ? Number(val) : "";
    const defaults = pid ? resolveProductDefaults(pid) : { unitPrice: "", taxConfigID: 0, taxPercentage: "", pricingType: "Fixed", purityStandard: "" };
    setItemForm(prev => {
      const next = {
        ...prev,
        productID: pid,
        pricingType: defaults.pricingType,
        purityStandard: defaults.purityStandard,
        unitPrice: defaults.pricingType === "Fixed" ? (defaults.unitPrice || prev.unitPrice || "") : "",
        metalRate: "",
        makingCharges: "",
        taxConfigID: defaults.taxConfigID || 0,
        taxPercentage: defaults.taxPercentage !== "" ? defaults.taxPercentage : prev.taxPercentage || "",
      };
      return calcLine(next);
    });
    setItemFormErrors(prev => ({ ...prev, productID: "", metalRate: "", unitPrice: "", billedWeight: "" }));
  };

  const handleItemFormBarcodeChange = (rawVal) => {
    if (!rawVal || rawVal === "0" || rawVal === "") {
      setItemForm(prev => calcLine({ ...prev, inventoryItemID: rawVal, billedWeight: "" }));
      setFormItemMeta(null);
      return;
    }

    const id = Number(rawVal);
    setItemForm(prev => ({ ...prev, inventoryItemID: rawVal }));

    if (!id || id <= 0) return;

    if (inventoryItemMap.has(id)) {
      const item = inventoryItemMap.get(id);
      setFormItemMeta(item);
      const billedWeight = item.netWeight != null
        ? String(item.netWeight)
        : item.grossWeight != null ? String(item.grossWeight) : "";
      setItemForm(prev => {
        let updated = { ...prev, billedWeight, inventoryItemID: rawVal };
        if (!prev.productID && item.productID) {
          const defaults = resolveProductDefaults(item.productID);
          updated = {
            ...updated,
            productID: Number(item.productID),
            pricingType: defaults.pricingType,
            purityStandard: defaults.purityStandard,
            unitPrice: defaults.pricingType === "Fixed" ? (defaults.unitPrice || "") : "",
            metalRate: "",
            makingCharges: "",
            taxConfigID: defaults.taxConfigID || 0,
            taxPercentage: defaults.taxPercentage !== "" ? defaults.taxPercentage : "",
          };
        }
        return calcLine(updated);
      });
    } else {
      setFormItemMeta({ loading: true });
      inventoryItemDtlApi.getById(id)
        .then(res => {
          const item = res?.data ?? res;
          if (item && (item.inventoryItemID || item.productID != null)) {
            setFormItemMeta(item);
            const billedWeight = item.netWeight != null
              ? String(item.netWeight)
              : item.grossWeight != null ? String(item.grossWeight) : "";
            setItemForm(prev => {
              let updated = { ...prev, billedWeight, inventoryItemID: rawVal };
              if (!prev.productID && item.productID) {
                const defaults = resolveProductDefaults(item.productID);
                updated = {
                  ...updated,
                  productID: Number(item.productID),
                  pricingType: defaults.pricingType,
                  purityStandard: defaults.purityStandard,
                  unitPrice: defaults.pricingType === "Fixed" ? (defaults.unitPrice || "") : "",
                  metalRate: "",
                  makingCharges: "",
                  taxConfigID: defaults.taxConfigID || 0,
                  taxPercentage: defaults.taxPercentage !== "" ? defaults.taxPercentage : "",
                };
              }
              return calcLine(updated);
            });
          } else {
            setFormItemMeta({ error: `Item #${id} not found` });
          }
        })
        .catch(() => {
          setFormItemMeta({ error: `Item #${id} not found` });
        });
    }
  };

  const updateItemFormField = (field, val) => {
    setItemForm(prev => calcLine({ ...prev, [field]: val }));
    setItemFormErrors(prev => ({ ...prev, [field]: "" }));
  };

  const handleSaveItemForm = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const errs = {};
    if (!itemForm.productID) errs.productID = "Select a product";
    if (!itemForm.quantity || Number(itemForm.quantity) <= 0) errs.quantity = "Qty must be > 0";

    if (itemForm.productID) {
      const avail = getProductAvailableStock(itemForm.productID);
      const reqQty = Number(itemForm.quantity) || 0;
      if (avail <= 0) {
        errs.quantity = "Selected product is out of stock (0 in vault)";
      } else if (reqQty > avail) {
        errs.quantity = `Only ${avail} available in vault`;
      }
    }

    if (itemForm.inventoryItemID) {
      const tagId = Number(itemForm.inventoryItemID);
      const tagItem = inventoryItemMap.get(tagId);
      if (tagItem && (Number(tagItem.quantity) || 0) <= 0) {
        errs.inventoryItemID = `Tag #${tagId} is already marked as sold`;
      }
    }

    const isWeightBased = ["per gram", "per carat"].includes(
      (itemForm.pricingType || "").toLowerCase()
    );
    if (isWeightBased) {
      if (!itemForm.metalRate || Number(itemForm.metalRate) <= 0) {
        errs.metalRate = "Metal rate is required";
      }
      if (!itemForm.billedWeight || Number(itemForm.billedWeight) <= 0) {
        errs.billedWeight = "Net weight is required for weight-based pricing";
      }
    } else {
      if (itemForm.unitPrice === "" || itemForm.unitPrice == null || Number(itemForm.unitPrice) < 0) {
        errs.unitPrice = "Unit price is required";
      }
    }

    if (itemForm.makingCharges !== "" && itemForm.makingCharges != null) {
      if (isNaN(Number(itemForm.makingCharges)) || Number(itemForm.makingCharges) < 0) {
        errs.makingCharges = "Making charges cannot be negative.";
      }
    }

    if (itemForm.requestedSize !== "" && itemForm.requestedSize != null) {
      if (isNaN(Number(itemForm.requestedSize)) || Number(itemForm.requestedSize) <= 0) {
        errs.requestedSize = "Requested size must be greater than zero.";
      }
    }

    if (Object.keys(errs).length > 0) {
      setItemFormErrors(errs);
      return;
    }

    const calculatedLine = calcLine(itemForm);

    if (editingIndex !== null) {
      setLines(prev => prev.map((l, i) => i === editingIndex ? calculatedLine : l));
      if (formItemMeta) {
        setItemMeta(prev => ({ ...prev, [editingIndex]: formItemMeta }));
      }
      setEditingIndex(null);
    } else {
      const nextIdx = lines.length;
      setLines(prev => [...prev, calculatedLine]);
      if (formItemMeta) {
        setItemMeta(prev => ({ ...prev, [nextIdx]: formItemMeta }));
      }
    }

    setItemForm(blankItemForm());
    setFormItemMeta(null);
    setItemFormErrors({});
    setErrors(prev => ({ ...prev, lines: "" }));
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setItemForm(blankItemForm());
    setFormItemMeta(null);
    setItemFormErrors({});
  };

  const handleClearItemForm = () => {
    setItemForm(blankItemForm());
    setFormItemMeta(null);
    setItemFormErrors({});
    setErrors(prev => ({ ...prev, lines: "" }));
  };

  const handleEditRow = (idx) => {
    const target = lines[idx];
    if (!target) return;
    setEditingIndex(idx);
    const prod = target.productID ? productMap.get(Number(target.productID)) : null;
    setItemForm({
      ...target,
      pricingType: target.pricingType || prod?.pricingType || "Fixed",
      purityStandard: target.purityStandard || prod?.purityStandard || "",
    });
    setFormItemMeta(itemMeta[idx] || null);
    setItemFormErrors({});
  };

  const handleDeleteRow = (idx) => {
    if (editingIndex === idx) {
      handleCancelEdit();
    } else if (editingIndex !== null && editingIndex > idx) {
      setEditingIndex(editingIndex - 1);
    }
    setLines(prev => prev.filter((_, i) => i !== idx));
    setItemMeta(prev => {
      const next = {};
      Object.keys(prev).forEach(k => {
        const ki = Number(k);
        if (ki < idx) next[ki] = prev[ki];
        else if (ki > idx) next[ki - 1] = prev[ki];
      });
      return next;
    });
  };

  /* ── Barcode Scanner State & Logic ── */
  const [scanCode, setScanCode] = useState("");
  const [scanLoading, setScanLoading] = useState(false);
  const [scanStatus, setScanStatus] = useState(null);
  const barcodeInputRef = useRef(null);

  const modalScanBuffer = useRef("");
  const modalLastKeyTime = useRef(0);
  const modalScanTimer = useRef(null);

  // Audio beep on scan (A5 880Hz for success, 320Hz for error/warn)
  const playBeep = (success = true) => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = success ? 880 : 320;
      gain.gain.value = 0.12;
      osc.start();
      osc.stop(ctx.currentTime + (success ? 0.1 : 0.2));
    } catch (_) { }
  };

  const handleBarcodeScan = useCallback(async (rawCode) => {
    const code = String(rawCode || "").trim();
    if (!code) return;
    const num = parseInt(code, 10);
    if (!num || isNaN(num)) {
      setScanStatus({ type: "error", message: `Invalid barcode tag: "${code}"` });
      playBeep(false);
      return;
    }

    setScanLoading(true);
    setScanStatus(null);

    try {
      // 1. Look up in inventoryItemMap or array (checking inventoryItemID & barcodeID)
      let item = inventoryItemMap.get(num) ||
        (inventoryItems || []).find(i => Number(i.inventoryItemID) === num || Number(i.barcodeID) === num);

      // 2. Fallback to API if not present in memory
      if (!item) {
        try {
          const res = await inventoryItemDtlApi.getById(num);
          item = res?.data ?? res;
        } catch (_) {
          const allRes = await inventoryItemDtlApi.getAll().catch(() => []);
          const allList = Array.isArray(allRes) ? allRes : (allRes?.data ?? []);
          item = allList.find(i => Number(i.inventoryItemID) === num || Number(i.barcodeID) === num) || null;
        }
      }

      if (!item || (!item.inventoryItemID && item.productID == null)) {
        setScanStatus({ type: "error", message: `Tag #${code} not found in inventory.` });
        playBeep(false);
        setScanLoading(false);
        return;
      }

      if ((Number(item.quantity) || 0) <= 0) {
        setScanStatus({
          type: "error",
          message: `Tag #${code} is already marked as sold.`,
        });
        playBeep(false);
        setScanCode("");
        setScanLoading(false);
        return;
      }

      const invItemId = Number(item.inventoryItemID);

      // 3. Check if already added to lines
      const existingIdx = lines.findIndex(l => Number(l.inventoryItemID) === invItemId);
      if (existingIdx !== -1) {
        setScanStatus({
          type: "warn",
          message: `Item #${invItemId} is already added in Line ${existingIdx + 1}.`,
        });
        playBeep(false);
        setScanCode("");
        setScanLoading(false);
        return;
      }

      // 4. Resolve product defaults
      const prodId = item.productID ? String(item.productID) : "";
      const defaults = prodId ? resolveProductDefaults(prodId) : {};
      const billedWeight = item.netWeight != null
        ? String(item.netWeight)
        : item.grossWeight != null ? String(item.grossWeight) : "";

      const product = prodId ? productMap.get(Number(prodId)) : null;

      const newLine = calcLine({
        invoiceItemID: 0,
        productID: prodId,
        inventoryItemID: String(invItemId),
        quantity: 1,
        billedWeight,
        metalRate: "",
        makingCharges: "",
        ...defaults,
      });

      // 5. Append or replace blank initial line
      setLines(prev => {
        if (prev.length === 1 && !prev[0].productID && !prev[0].inventoryItemID && !prev[0].quantity) {
          setItemMeta({ 0: item });
          return [newLine];
        }
        const nextIdx = prev.length;
        setItemMeta(m => ({ ...m, [nextIdx]: item }));
        return [...prev, newLine];
      });

      // Clear lines validation error
      setErrors(prev => ({ ...prev, lines: "" }));

      playBeep(true);
      setScanStatus({
        type: "success",
        message: `✓ Added Item #${invItemId} (${product?.productName || "Product"} · ${billedWeight || 0}g)`,
      });
      setScanCode("");
      setTimeout(() => {
        barcodeInputRef.current?.focus();
      }, 50);
    } catch (err) {
      setScanStatus({ type: "error", message: `Could not load tag #${code}: ${err.message || "Network error"}` });
      playBeep(false);
    } finally {
      setScanLoading(false);
    }
  }, [inventoryItemMap, inventoryItems, lines, productMap, resolveProductDefaults, calcLine]);

  /* Auto-focus barcode input when opening a new invoice */
  useEffect(() => {
    if (!isLocked && !isEdit) {
      const t = setTimeout(() => barcodeInputRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
  }, [isLocked, isEdit]);

  /* Auto-resolve initial lines that have productID but empty price/tax */
  useEffect(() => {
    setLines(prev => {
      let changed = false;
      const updated = prev.map(line => {
        if (line.productID && (!line.pricingType || line.unitPrice === "" || line.unitPrice === undefined)) {
          const defaults = resolveProductDefaults(line.productID);
          if (defaults.unitPrice || defaults.taxPercentage || defaults.pricingType) {
            changed = true;
            return calcLine({
              ...line,
              pricingType: line.pricingType || defaults.pricingType,
              purityStandard: line.purityStandard || defaults.purityStandard,
              unitPrice: defaults.pricingType === "Fixed" ? (defaults.unitPrice || line.unitPrice) : line.unitPrice,
              taxConfigID: defaults.taxConfigID || line.taxConfigID,
              taxPercentage: defaults.taxPercentage || line.taxPercentage,
            });
          }
        }
        return line;
      });
      return changed ? updated : prev;
    });
  }, [resolveProductDefaults, calcLine]);

  /* Global keystroke listener inside modal for barcode gun wedge */
  useEffect(() => {
    if (isLocked) return;

    const onModalKeyDown = (e) => {
      if (!e) return;
      const activeEl = document.activeElement;

      // If focused inside barcodeInputRef, input's own onKeyDown handles Enter
      if (barcodeInputRef?.current && activeEl === barcodeInputRef.current) return;

      const buffer = (modalScanBuffer && typeof modalScanBuffer.current === "string") ? modalScanBuffer.current : "";

      // If user is typing in standard text inputs (Customer name, notes, product search inside dropdown, etc.)
      if (activeEl && (activeEl.tagName === "INPUT" || activeEl.tagName === "TEXTAREA" || activeEl.tagName === "SELECT")) {
        const now = Date.now();
        const lastTime = modalLastKeyTime?.current || 0;
        const gap = now - lastTime;
        if (modalLastKeyTime) modalLastKeyTime.current = now;

        if (gap > 45 && buffer && buffer.length > 0) {
          if (modalScanBuffer) modalScanBuffer.current = "";
        }
        if (e.key === "Enter" && buffer && buffer.length >= 1) {
          const scanned = buffer.trim();
          if (modalScanBuffer) modalScanBuffer.current = "";
          e.preventDefault();
          handleBarcodeScan(scanned);
          return;
        }
        if (e.key && e.key.length === 1 && gap <= 45) {
          if (modalScanBuffer) {
            modalScanBuffer.current = buffer + e.key;
          }
        }
        return;
      }

      // If cashier clicked anywhere on modal surface:
      const now = Date.now();
      const lastTime = modalLastKeyTime?.current || 0;
      const gap = now - lastTime;
      if (modalLastKeyTime) modalLastKeyTime.current = now;

      if (e.key === "Enter") {
        const val = buffer.trim();
        if (modalScanBuffer) modalScanBuffer.current = "";
        if (modalScanTimer?.current) clearTimeout(modalScanTimer.current);
        if (val && val.length >= 1) {
          e.preventDefault();
          handleBarcodeScan(val);
        }
        return;
      }

      if (!e.key || e.key.length !== 1) return;

      if (gap > 400) {
        if (modalScanBuffer) modalScanBuffer.current = e.key;
      } else {
        if (modalScanBuffer) modalScanBuffer.current = buffer + e.key;
      }

      if (modalScanTimer?.current) clearTimeout(modalScanTimer.current);
      if (modalScanTimer) {
        modalScanTimer.current = setTimeout(() => {
          const currentBuf = (modalScanBuffer && typeof modalScanBuffer.current === "string") ? modalScanBuffer.current : "";
          const val = currentBuf.trim();
          if (modalScanBuffer) modalScanBuffer.current = "";
          if (val && val.length >= 2) {
            handleBarcodeScan(val);
          }
        }, 150);
      }
    };

    window.addEventListener("keydown", onModalKeyDown);
    return () => {
      window.removeEventListener("keydown", onModalKeyDown);
      clearTimeout(modalScanTimer.current);
    };
  }, [isLocked, handleBarcodeScan]);

  /* ── Totals ── */
  const totalAmount = lines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const totalTaxAmount = lines.reduce((s, l) => s + (l.taxAmount || 0), 0);
  const grossAmount = lines.reduce((s, l) => s + (l.totalPrice || 0), 0);

  /* ── Validate ── */
  const validate = () => {
    const e = {};
    if (!form.invoiceNumber.trim()) e.invoiceNumber = "Invoice number is required.";
    if (!form.invoiceDate) e.invoiceDate = "Invoice date is required.";

    const nameTrim = form.customerName.trim();
    if (!nameTrim) {
      e.customerName = "Customer name is required.";
    } else if (nameTrim.length > 150) {
      e.customerName = "Customer name cannot exceed 150 characters.";
    }

    const phoneClean = (form.customerPhone || "").trim().replace(/\D/g, "");
    if ((form.customerPhone || "").trim() && phoneClean.length !== 10) {
      e.customerPhone = "Customer phone must be 10 digits.";
    }

    const gstin = (form.customerGSTIN || "").trim().toUpperCase();
    if (gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(gstin)) {
      e.customerGSTIN = "Invalid GSTIN format (e.g. 24AAAAA0000A1Z5).";
    }

    if ((form.customerAddress || "").trim().length > 500) {
      e.customerAddress = "Customer address cannot exceed 500 characters.";
    }

    if (editingIndex !== null) {
      e.lines = "You have an uncommitted line edit. Click 'Update Line' or 'Cancel Edit' before saving.";
    } else if (lines.length === 0) {
      if (itemForm.productID && itemForm.unitPrice !== "") {
        e.lines = "Click '+ Add Item to Table' to add your entered item first, or 'Clear' to reset.";
      } else {
        e.lines = "Please add at least one line item to the invoice.";
      }
    }
    return Object.keys(e).length === 0 ? null : e;
  };

  const handleSubmit = (status) => {
    const e = validate();
    if (e) { setErrors(e); return; }

    // Pre-flight check: Finalising requires sufficient stock in vault
    if (status === "Finalised") {
      for (let i = 0; i < lines.length; i++) {
        const l = lines[i];
        const pid = Number(l.productID);
        const prod = productMap.get(pid);
        const avail = getProductAvailableStock(pid);
        const reqQty = Number(l.quantity) || 0;
        if (avail < reqQty) {
          setErrors({
            lines: `Cannot finalise invoice: "${prod?.productName || `Product #${pid}`}" has insufficient stock (${avail} available in vault, ${reqQty} requested). Please adjust quantity or Save as Draft.`
          });
          return;
        }
      }
    }
    onSave({
      header: {
        ...form,
        invoiceStatus: status,
        totalAmount: parseFloat(totalAmount.toFixed(2)),
        totalTaxAmount: parseFloat(totalTaxAmount.toFixed(2)),
        grossAmount: parseFloat(grossAmount.toFixed(2)),
        ...(isEdit ? { invoiceID: initial.invoiceID } : {}),
      },
      lines: lines.map(l => {
        const isWeightBased = ["per gram", "per carat"].includes(
          (l.pricingType || "").toLowerCase()
        );
        return {
          invoiceItemID: Number(l.invoiceItemID) || 0,
          productID: Number(l.productID),
          inventoryItemID: l.inventoryItemID ? Number(l.inventoryItemID) : null,
          quantity: Number(l.quantity),
          billedWeight: isWeightBased && parseFloat(l.billedWeight) > 0 
            ? parseFloat(l.billedWeight) 
            : null,
          unitPrice: parseFloat(l.unitPrice) || 0,
          metalRate: parseFloat(l.metalRate) || 0,
          makingCharges: parseFloat(l.makingCharges) || 0,
          pricingType: l.pricingType || "Fixed",
          requestedSize: l.requestedSize ? Number(l.requestedSize) : null,
          isCustomSize: Boolean(l.isCustomSize),
          purityStandard: l.purityStandard || "",
          taxConfigID: Number(l.taxConfigID) || 0,
          taxPercentage: parseFloat(l.taxPercentage) || 0,
          lineAmount: l.lineAmount,
          taxAmount: l.taxAmount,
          totalPrice: l.totalPrice,
        };
      }),
    });
  };

  const fmtAmt = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;

  /* ── Price hint for a line ── */
  const getPriceHint = (productID, unitPrice) => {
    if (!productID) return null;
    const prod = productMap.get(Number(productID));
    if (prod && ["per gram", "per carat"].includes((prod.pricingType || "").toLowerCase())) {
      return null;
    }
    const price = latestPriceMap.get(Number(productID));
    if (!price) {
      const hasManualPrice = unitPrice !== "" && unitPrice != null && Number(unitPrice) > 0;
      if (hasManualPrice) {
        return { text: `Manual price: ₹${parseFloat(unitPrice).toFixed(2)} (no price configured in master)`, warn: false };
      }
      return { text: "No price configured for this product (enter unit price manually below)", warn: true };
    }
    const date = new Date(price.effdt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const isOverride = unitPrice !== "" && unitPrice != null && Number(unitPrice) !== Number(price.grossSellingPrice);
    if (isOverride) {
      return { text: `Master Price: ₹${price.grossSellingPrice} (as of ${date}) · Manual override: ₹${parseFloat(unitPrice).toFixed(2)}`, warn: false };
    }
    return { text: `Price as of ${date} · Landing ₹${price.landingCost} · Margin ${price.marginPercent}%`, warn: false };
  };

  /* ── Tax hint for a line ── */
  const getTaxHint = (productID, taxPercentage) => {
    if (!productID) return null;
    const product = productMap.get(Number(productID));
    if (!product) return null;

    const resolvedTax = resolveCategoryTax(product.categoryID);
    if (resolvedTax.taxConfig) {
      return { text: `${resolvedTax.taxConfig.taxName} · ${resolvedTax.taxConfig.taxPercentage}%`, warn: false };
    }

    // Hide the warning if user has manually entered a tax value > 0
    const hasManualTax = taxPercentage !== "" && taxPercentage != null && Number(taxPercentage) > 0;
    if (hasManualTax) {
      return null;
    }

    return { text: "No tax configured for category (enter tax % manually)", warn: true };
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form" style={{ maxWidth: 940, width: "100%", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>

        <div className="rv-modal-header" style={{ flexShrink: 0, paddingRight: 48 }}>
          <div className="rv-modal-icon"><InvoiceIcon /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <h3 className="rv-modal-title" style={{ marginBottom: 0 }}>
                {isLocked
                  ? `Invoice ${initial.invoiceNumber}`
                  : isEdit ? "Edit Bill / Invoice" : "Create New Bill"}
              </h3>
              {initial?.invoiceStatus && (
                <span className={`rv-inv-status-badge rv-inv-status--${STATUS_CONFIG[initial.invoiceStatus]?.color ?? "draft"}`}>
                  {initial.invoiceStatus}
                </span>
              )}
            </div>
            <p className="rv-modal-sub" style={{ marginTop: 3 }}>
              {isLocked
                ? `Status: ${initial.invoiceStatus} — read only`
                : isEdit ? "Update bill details and items." : "Create a new customer bill or estimate."}
            </p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body" style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>

          {/* ── Header fields ── */}
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Bill / Invoice Number <span className="rv-required">*</span></label>
              <input
                className={`rv-input${errors.invoiceNumber ? " rv-input--err" : ""}`}
                placeholder="e.g. INV-2026-001"
                value={form.invoiceNumber}
                disabled={isLocked}
                onChange={e => { setForm(f => ({ ...f, invoiceNumber: e.target.value })); setErrors(v => ({ ...v, invoiceNumber: "" })); }}
              />
              {errors.invoiceNumber && <p className="rv-field-err">{errors.invoiceNumber}</p>}
            </div>
            <div className="rv-field">
              <label className="rv-label">Bill Date <span className="rv-required">*</span></label>
              <input
                className={`rv-input rv-input--date${errors.invoiceDate ? " rv-input--err" : ""}`}
                type="date"
                value={form.invoiceDate}
                disabled={isLocked}
                onChange={e => { setForm(f => ({ ...f, invoiceDate: e.target.value })); setErrors(v => ({ ...v, invoiceDate: "" })); }}
              />
              {errors.invoiceDate && <p className="rv-field-err">{errors.invoiceDate}</p>}
            </div>
          </div>

          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Customer Name <span className="rv-required">*</span></label>
              <input
                className={`rv-input${errors.customerName ? " rv-input--err" : ""}`}
                placeholder="Full name"
                maxLength={150}
                value={form.customerName}
                disabled={isLocked}
                onChange={e => { setForm(f => ({ ...f, customerName: e.target.value })); setErrors(v => ({ ...v, customerName: "" })); }}
              />
              {errors.customerName && <p className="rv-field-err">{errors.customerName}</p>}
            </div>
            <div className="rv-field">
              <label className="rv-label">Customer Phone</label>
              <input
                className={`rv-input${errors.customerPhone ? " rv-input--err" : ""}`}
                placeholder="e.g. 9876543210"
                maxLength={10}
                value={form.customerPhone}
                disabled={isLocked}
                onChange={e => {
                  const val = e.target.value.replace(/\D/g, "");
                  setForm(f => ({ ...f, customerPhone: val }));
                  setErrors(v => ({ ...v, customerPhone: "" }));
                }}
              />
              {errors.customerPhone && <p className="rv-field-err">{errors.customerPhone}</p>}
            </div>
          </div>

          <div className="rv-field">
            <label className="rv-label">Customer Address</label>
            <textarea
              className={`rv-input${errors.customerAddress ? " rv-input--err" : ""}`}
              rows={2}
              placeholder="Address (optional)"
              maxLength={500}
              value={form.customerAddress || ""}
              disabled={isLocked}
              onChange={e => {
                setForm(f => ({ ...f, customerAddress: e.target.value }));
                setErrors(v => ({ ...v, customerAddress: "" }));
              }}
            />
            {errors.customerAddress && <p className="rv-field-err">{errors.customerAddress}</p>}
          </div>

          <div className="rv-field">
            <label className="rv-label">Customer GSTIN</label>
            <input
              className={`rv-input${errors.customerGSTIN ? " rv-input--err" : ""}`}
              placeholder="Optional — e.g. 24AAAAA0000A1Z5"
              maxLength={15}
              value={form.customerGSTIN}
              disabled={isLocked}
              onChange={e => {
                const val = e.target.value.toUpperCase();
                setForm(f => ({ ...f, customerGSTIN: val }));
                setErrors(v => ({ ...v, customerGSTIN: "" }));
              }}
            />
            {errors.customerGSTIN && <p className="rv-field-err">{errors.customerGSTIN}</p>}
          </div>

          {/* ── Quick Barcode Gun Scanner Bar ── */}
          {!isLocked && (
            <div className="rv-inv-scanner-card" style={{
              background: "linear-gradient(135deg, rgba(179,135,67,.06) 0%, rgba(185,167,146,.08) 100%)",
              border: "1px solid rgba(179,135,67,.25)",
              borderRadius: 12,
              padding: "10px 14px",
              marginBottom: 16,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <BarcodeIcon />
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--dark, #2c2724)", letterSpacing: ".2px", whiteSpace: "nowrap" }}>
                    Scan Barcode Tag
                  </span>
                </div>
                <span style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#2a8a96",
                  background: "rgba(42,138,150,.12)",
                  padding: "2px 8px",
                  borderRadius: 12,
                  flexShrink: 0,
                  whiteSpace: "nowrap"
                }}>
                  Active
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1, position: "relative" }}>
                  <input
                    ref={barcodeInputRef}
                    className="rv-input"
                    style={{ height: 36, fontSize: 13, paddingRight: scanLoading ? 36 : 12 }}
                    placeholder="Scan barcode or type tag number and press Enter"
                    value={scanCode}
                    onChange={e => setScanCode(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleBarcodeScan(scanCode);
                      }
                    }}
                  />
                  {scanLoading && (
                    <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}>
                      <span className="rv-spinner" style={{ width: 14, height: 14 }} />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="rv-btn rv-btn--primary"
                  style={{
                    height: 36,
                    padding: "0 14px",
                    fontSize: 12.5,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    flexShrink: 0,
                    background: "linear-gradient(135deg, #b38743 0%, #8c6527 100%)",
                    border: "none",
                    color: "#fff"
                  }}
                  onClick={() => handleBarcodeScan(scanCode)}
                  disabled={scanLoading || !scanCode.trim()}
                >
                  <PlusIcon /> Add Tag
                </button>
              </div>

              {scanStatus && (
                <div style={{
                  fontSize: 11.5,
                  padding: "6px 10px",
                  borderRadius: 6,
                  marginTop: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: scanStatus.type === "success"
                    ? "rgba(109,138,109,.15)"
                    : scanStatus.type === "warn"
                      ? "rgba(230,126,34,.15)"
                      : "rgba(192,57,43,.15)",
                  color: scanStatus.type === "success"
                    ? "#2d6a4f"
                    : scanStatus.type === "warn"
                      ? "#d35400"
                      : "#c0392b",
                  fontWeight: 600,
                }}>
                  <span>{scanStatus.message}</span>
                  <button
                    type="button"
                    onClick={() => setScanStatus(null)}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, color: "inherit", padding: 0, lineHeight: 1 }}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Line items section ── */}
          <LineItemsEditor
            title="Items Purchased"
            required
            items={lines}
            isLocked={isLocked}
            editingIndex={editingIndex}
            editingTitle={`Editing Item #${editingIndex !== null ? editingIndex + 1 : ""}`}
            addTitle="Add Item"
            error={errors.lines}
            onEdit={handleEditRow}
            onDelete={handleDeleteRow}
            onCancelEdit={handleCancelEdit}
            onClear={handleClearItemForm}
            emptyTitle="No items added yet"
            emptyMessage="Select a product above and click 'Add Item to Table', or scan a barcode tag."
            renderForm={({ isEditing }) => {
              const itemPriceHint = getPriceHint(itemForm.productID, itemForm.unitPrice);
              const itemTaxHint = getTaxHint(itemForm.productID, itemForm.taxPercentage);
              const currentCalculated = calcLine(itemForm);

              return (
                <div>
                  {/* Row 1: Product & Barcode Tag */}
                  <div className="rv-field-row-2col" style={{ marginBottom: 6 }}>
                    <div className="rv-field" style={{ marginBottom: 0 }}>
                      <label className="rv-label" style={{ fontSize: 11 }}>Product <span className="rv-required">*</span></label>
                      <SearchableDropdown
                        options={products}
                        valueKey="productID"
                        labelKey="productName"
                        value={itemForm.productID != null && itemForm.productID !== "" ? Number(itemForm.productID) : ""}
                        onChange={val => handleItemFormProductChange(val)}
                        placeholder="— Select product —"
                        searchPlaceholder="Search products…"
                        error={itemFormErrors.productID}
                        pageSize={10}
                      />
                      {itemForm.productID && (() => {
                        const avail = getProductAvailableStock(itemForm.productID);
                        return (
                          <div style={{ fontSize: 11, marginTop: 4 }}>
                            {avail > 0 ? (
                              <span style={{ color: "#27ae60", fontWeight: 600 }}>
                                ✓ In Stock: {avail} available in vault
                              </span>
                            ) : (
                              <span style={{ color: "#c0392b", fontWeight: 600, background: "rgba(192,57,43,.08)", padding: "2px 6px", borderRadius: 4 }}>
                                ⚠ Out of Stock (0 available in vault)
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div className="rv-field" style={{ marginBottom: 0 }}>
                      <label className="rv-label" style={{ fontSize: 11 }}>Barcode Tag # <span style={{ color: "var(--taupe)", fontWeight: 400 }}>(optional)</span></label>
                      <input
                        className="rv-input"
                        type="number" min="0" placeholder="Scan or enter tag number"
                        value={itemForm.inventoryItemID}
                        onChange={e => handleItemFormBarcodeChange(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSaveItemForm(); } }}
                      />
                    </div>
                  </div>

                  {/* Inventory Item Info Line */}
                  {formItemMeta?.loading && (
                    <p style={{ fontSize: 11, margin: "2px 0 6px", color: "var(--taupe)" }}>
                      ⏳ Fetching inventory item #{itemForm.inventoryItemID} details…
                    </p>
                  )}

                  {formItemMeta?.error && (
                    <p style={{ fontSize: 11, margin: "2px 0 6px", color: "#c0392b" }}>
                      ⚠ {formItemMeta.error}
                    </p>
                  )}

                  {formItemMeta && !formItemMeta.loading && !formItemMeta.error && (
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      margin: "2px 0 6px",
                      padding: "4px 10px",
                      borderRadius: 6,
                      background: "rgba(185,167,146,.12)",
                      fontSize: 11,
                      color: "#6a5a52",
                      flexWrap: "wrap",
                    }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ fontSize: 12 }}>⚖</span>
                        <span>
                          Gross: <strong>{parseFloat(formItemMeta.grossWeight || 0).toFixed(3)}g</strong>
                          {" · "}
                          Less: <strong>{parseFloat(formItemMeta.lessWeight || 0).toFixed(3)}g</strong>
                          {" · "}
                          Net: <strong style={{ color: "var(--dark, #2c2724)" }}>{parseFloat(formItemMeta.netWeight || 0).toFixed(3)}g</strong>
                        </span>
                      </span>
                      <span style={{ fontSize: 10, color: "#4d7a4d", fontWeight: 600, background: "rgba(109,138,109,.15)", padding: "1px 6px", borderRadius: 10 }}>
                        ✓ Weight auto-filled
                      </span>
                    </div>
                  )}

                  {/* Price & Tax hints */}
                  {!["per gram", "per carat"].includes((itemForm.pricingType || "").toLowerCase()) && itemPriceHint && (
                    <p style={{ fontSize: 10.5, marginBottom: 6, marginTop: 2, padding: "3px 8px", borderRadius: 5, background: itemPriceHint.warn ? "rgba(192,57,43,.07)" : "rgba(185,167,146,.1)", color: itemPriceHint.warn ? "#c0392b" : "var(--taupe)" }}>
                      {itemPriceHint.warn ? "⚠ " : "ℹ "}{itemPriceHint.text}
                    </p>
                  )}
                  {itemTaxHint && (
                    <p style={{ fontSize: 10.5, marginBottom: 6, marginTop: 2, padding: "3px 8px", borderRadius: 5, background: itemTaxHint.warn ? "rgba(192,57,43,.07)" : "rgba(185,167,146,.1)", color: itemTaxHint.warn ? "#c0392b" : "var(--taupe)" }}>
                      {itemTaxHint.warn ? "⚠ " : "ℹ "}{itemTaxHint.text}
                    </p>
                  )}

                  {/* Row 2: Qty, Net Weight, Unit Price / Metal Rate & Making Charges, Tax % */}
                  {(() => {
                    const isWeightBased = ["per gram", "per carat"].includes(
                      (itemForm.pricingType || "").toLowerCase()
                    );

                    return (
                      <>
                        {isWeightBased && (
                          <div style={{ background: "rgba(185,167,146,.1)", borderRadius: 8, padding: "8px 10px", marginBottom: 6, fontSize: 11 }}>
                            <strong>Weight-based pricing</strong> — enter today's metal rate and making charges.
                            {" "}Purity: <strong>{itemForm.purityStandard || "—"}</strong>
                            {itemForm.purityStandard && (
                              <span style={{ color: "#27ae60", marginLeft: 6 }}>
                                (factor: {parsePurityFactor(itemForm.purityStandard).toFixed(4)})
                              </span>
                            )}
                          </div>
                        )}

                        <div style={{ display: "grid", gridTemplateColumns: isWeightBased ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1.3fr 1fr", gap: 8, marginBottom: 6 }}>

                          {/* Quantity */}
                          <div className="rv-field" style={{ marginBottom: 0 }}>
                            <label className="rv-label" style={{ fontSize: 11 }}>Quantity <span className="rv-required">*</span></label>
                            <input
                              className={`rv-input${itemFormErrors.quantity ? " rv-input--err" : ""}`}
                              type="number" min="1" step="1" placeholder="1"
                              value={itemForm.quantity}
                              onChange={e => updateItemFormField("quantity", e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSaveItemForm(); } }}
                            />
                            {itemFormErrors.quantity && <p className="rv-field-err">{itemFormErrors.quantity}</p>}
                          </div>

                          {/* Net Weight — always visible */}
                          <div className="rv-field" style={{ marginBottom: 0 }}>
                            <label className="rv-label" style={{ fontSize: 11 }}>Net Weight (g)</label>
                            <input
                              className={`rv-input${itemFormErrors.billedWeight ? " rv-input--err" : ""}`}
                              type="number" min="0" step="0.001" placeholder="0.000"
                              value={itemForm.billedWeight}
                              onChange={e => updateItemFormField("billedWeight", e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSaveItemForm(); } }}
                            />
                            {itemFormErrors.billedWeight && <p className="rv-field-err">{itemFormErrors.billedWeight}</p>}
                          </div>

                          {isWeightBased ? (
                            <>
                              {/* Metal Rate per gram */}
                              <div className="rv-field" style={{ marginBottom: 0 }}>
                                <label className="rv-label" style={{ fontSize: 11 }}>
                                  Metal Rate (₹/g) <span className="rv-required">*</span>
                                </label>
                                <input
                                  className={`rv-input${itemFormErrors.metalRate ? " rv-input--err" : ""}`}
                                  type="number" min="0" step="0.01" placeholder="e.g. 150"
                                  value={itemForm.metalRate}
                                  onChange={e => {
                                    const rate = e.target.value;
                                    const purity = parsePurityFactor(itemForm.purityStandard);
                                    const computedUnitPrice = rate ? (Number(rate) * purity).toFixed(4) : "";
                                    setItemForm(prev => {
                                      const next = { ...prev, metalRate: rate, unitPrice: computedUnitPrice };
                                      return calcLine(next);
                                    });
                                    setItemFormErrors(prev => ({ ...prev, metalRate: "" }));
                                  }}
                                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSaveItemForm(); } }}
                                />
                                {itemFormErrors.metalRate && <p className="rv-field-err">{itemFormErrors.metalRate}</p>}
                                {itemForm.metalRate && itemForm.purityStandard && (
                                  <p style={{ fontSize: 10, margin: "2px 0 0", color: "var(--taupe)" }}>
                                    Effective: ₹{(Number(itemForm.metalRate) * parsePurityFactor(itemForm.purityStandard)).toFixed(2)}/g
                                  </p>
                                )}
                              </div>

                              {/* Making Charges */}
                              <div className="rv-field" style={{ marginBottom: 0 }}>
                                <label className="rv-label" style={{ fontSize: 11 }}>Making Charges (₹)</label>
                                <input
                                  className={`rv-input${itemFormErrors.makingCharges ? " rv-input--err" : ""}`}
                                  type="number" min="0" step="0.01" placeholder="0.00"
                                  value={itemForm.makingCharges}
                                  onChange={e => {
                                    setItemForm(prev => {
                                      const next = { ...prev, makingCharges: e.target.value };
                                      return calcLine(next);
                                    });
                                    setItemFormErrors(prev => ({ ...prev, makingCharges: "" }));
                                  }}
                                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSaveItemForm(); } }}
                                />
                                {itemFormErrors.makingCharges && <p className="rv-field-err">{itemFormErrors.makingCharges}</p>}
                              </div>
                            </>
                          ) : (
                            <>
                              {/* Unit Price — existing behaviour for Fixed */}
                              <div className="rv-field" style={{ marginBottom: 0 }}>
                                <label className="rv-label" style={{ fontSize: 11 }}>Unit Price (₹) <span className="rv-required">*</span></label>
                                <input
                                  className={`rv-input${itemFormErrors.unitPrice ? " rv-input--err" : ""}`}
                                  type="number" min="0" step="0.01"
                                  placeholder="0.00"
                                  value={itemForm.unitPrice}
                                  onChange={e => updateItemFormField("unitPrice", e.target.value)}
                                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSaveItemForm(); } }}
                                  style={itemForm.unitPrice ? { fontWeight: 600 } : {}}
                                />
                                {itemFormErrors.unitPrice && <p className="rv-field-err">{itemFormErrors.unitPrice}</p>}
                              </div>

                              {/* Tax % & Rule Name */}
                              <div className="rv-field" style={{ marginBottom: 0 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <label className="rv-label" style={{ fontSize: 11 }}>Tax %</label>
                                  {itemTaxHint && (
                                    <span style={{ fontSize: 9.5, color: itemTaxHint.warn ? "#b45309" : "#2d6a4f", fontWeight: 600 }}>
                                      {itemTaxHint.text.split(" · ")[0]}
                                    </span>
                                  )}
                                </div>
                                <input
                                  className="rv-input"
                                  type="number" min="0" max="100" step="0.01" placeholder="0"
                                  value={itemForm.taxPercentage}
                                  onChange={e => updateItemFormField("taxPercentage", e.target.value)}
                                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSaveItemForm(); } }}
                                />
                              </div>
                            </>
                          )}
                        </div>

                        {/* Tax % row for weight-based (shown below) */}
                        {isWeightBased && (
                          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 8, marginBottom: 6 }}>
                            <div className="rv-field" style={{ marginBottom: 0 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <label className="rv-label" style={{ fontSize: 11 }}>Tax %</label>
                                {itemTaxHint && (
                                  <span style={{ fontSize: 9.5, color: itemTaxHint.warn ? "#b45309" : "#2d6a4f", fontWeight: 600 }}>
                                    {itemTaxHint.text.split(" · ")[0]}
                                  </span>
                                )}
                              </div>
                              <input
                                className="rv-input"
                                type="number" min="0" max="100" step="0.01" placeholder="0"
                                value={itemForm.taxPercentage}
                                onChange={e => setItemForm(prev => {
                                  const next = { ...prev, taxPercentage: e.target.value };
                                  return calcLine(next);
                                })}
                                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSaveItemForm(); } }}
                              />
                            </div>
                            <div style={{ fontSize: 11, color: "var(--taupe)", paddingTop: 18 }}>
                              Unit Price used: ₹{Number(itemForm.unitPrice || 0).toFixed(4)}/g (rate × purity)
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* Row 3: Live Preview & Action Buttons */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, paddingTop: 8, borderTop: "1px dashed rgba(185,167,146,.25)", flexWrap: "wrap", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11.5 }}>
                      <span style={{ color: "var(--taupe)" }}>
                        Item: <strong style={{ color: "var(--dark)" }}>{fmtAmt(currentCalculated.lineAmount)}</strong>
                      </span>
                      <span style={{ color: "var(--taupe)" }}>
                        Tax: <strong style={{ color: "var(--dark)" }}>{fmtAmt(currentCalculated.taxAmount)}</strong>
                      </span>
                      <span style={{ background: "rgba(185,167,146,.15)", padding: "2px 8px", borderRadius: 6, fontWeight: 700, color: "var(--dark)" }}>
                        Line Total: {fmtAmt(currentCalculated.totalPrice)}
                      </span>
                    </div>

                    <div style={{ display: "flex", gap: 8 }}>
                      {isEditing ? (
                        <button
                          type="button"
                          className="rv-btn rv-btn--ghost"
                          style={{ padding: "5px 12px", fontSize: 12 }}
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="rv-btn rv-btn--ghost"
                          style={{ padding: "5px 12px", fontSize: 12 }}
                          onClick={handleClearItemForm}
                          title="Clear form inputs"
                        >
                          Clear
                        </button>
                      )}
                      <button
                        type="button"
                        className="rv-btn rv-btn--primary"
                        style={{ padding: "5px 14px", fontSize: 12 }}
                        onClick={handleSaveItemForm}
                      >
                        {isEditing ? `✓ Update Line #${editingIndex + 1}` : <><PlusIcon /> Add Item to Table</>}
                      </button>
                    </div>
                  </div>
                </div>
              );
            }}
            columns={[
              {
                header: "Product",
                render: (line) => {
                  const product = line.productID ? productMap.get(Number(line.productID)) : null;
                  const category = product?.categoryID ? categoryMap.get(Number(product.categoryID)) : null;
                  return (
                    <div>
                      <div style={{ fontWeight: 600, color: "var(--dark)", fontSize: 12 }}>
                        {product?.productName || `Product #${line.productID}`}
                      </div>
                      <div style={{ display: "flex", gap: 5, marginTop: 2, alignItems: "center" }}>
                        {product?.productCode && (
                          <span style={{ fontSize: 9.5, fontWeight: 600, color: "var(--taupe)", background: "rgba(185,167,146,.18)", padding: "1px 5px", borderRadius: 10 }}>
                            {product.productCode}
                          </span>
                        )}
                        {category && (
                          <span style={{ fontSize: 9.5, fontWeight: 600, color: "#547854", background: "rgba(109,138,109,.12)", padding: "1px 5px", borderRadius: 10 }}>
                            {category.categoryName}
                          </span>
                        )}
                        {line.requestedSize && (
                          <span style={{ fontSize: 9.5, fontWeight: 700, color: "#1d4ed8", background: "rgba(59,130,246,.12)", padding: "1px 5px", borderRadius: 10 }}>
                            Size: {line.requestedSize} {line.isCustomSize ? "(Custom)" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                }
              },
              {
                header: "Tag #",
                render: (line) => line.inventoryItemID ? (
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#6a5a52", background: "rgba(185,167,146,.15)", padding: "2px 6px", borderRadius: 4 }}>
                    #{line.inventoryItemID}
                  </span>
                ) : (
                  <span style={{ color: "var(--taupe)", fontSize: 11 }}>—</span>
                )
              },
              {
                header: "Qty",
                align: "right",
                render: (line) => <strong>{line.quantity}</strong>
              },
              {
                header: "Net Wt",
                align: "right",
                render: (line) => line.billedWeight ? `${parseFloat(line.billedWeight).toFixed(3)}g` : "—"
              },
              {
                header: "Unit Price",
                align: "right",
                render: (line) => fmtAmt(line.unitPrice)
              },
              {
                header: "Making ₹",
                align: "right",
                render: (line) => Number(line.makingCharges) > 0 ? fmtAmt(line.makingCharges) : "—"
              },
              {
                header: "Tax %",
                align: "right",
                render: (line) => {
                  const resolved = resolveProductDefaults(line.productID);
                  const taxRule = taxRateMap.get(line.taxConfigID || resolved.taxConfigID);
                  return (
                    <div>
                      <div style={{ fontWeight: 600 }}>
                        {line.taxPercentage != null && line.taxPercentage !== "" ? `${line.taxPercentage}%` : "0%"}
                      </div>
                      {taxRule && (
                        <div style={{ fontSize: 9, color: "var(--taupe)", whiteSpace: "nowrap" }}>
                          {taxRule.taxName}
                        </div>
                      )}
                    </div>
                  );
                }
              },
              {
                header: "Total",
                align: "right",
                render: (line) => <strong>{fmtAmt(line.totalPrice)}</strong>
              }
            ]}
          />

          {/* ── Grand total bar ── */}
          {lines.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, padding: "12px 14px", background: "rgba(185,167,146,.1)", borderRadius: 10, marginTop: 4 }}>
              {[
                ["Subtotal", totalAmount],
                ["Total Tax", totalTaxAmount],
                ["Grand Total", grossAmount],
              ].map(([lbl, val]) => (
                <div key={lbl} style={{ textAlign: "center" }}>
                  <p style={{ fontSize: 11, color: "var(--taupe)", marginBottom: 3 }}>{lbl}</p>
                  <p style={{ fontSize: 15, fontWeight: 700, color: "var(--dark)" }}>{fmtAmt(val)}</p>
                </div>
              ))}
            </div>
          )}

        </div>

        {/* ── Footer buttons ── */}
        <div className="rv-modal-footer" style={{ flexShrink: 0 }}>
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>
            {isLocked ? "Close" : "Cancel"}
          </button>
          {!isLocked && (
            <>
              <button
                className="rv-btn rv-btn--ghost"
                style={{ borderColor: "var(--taupe)" }}
                onClick={() => handleSubmit("Draft")}
                disabled={saving}
              >
                {saving && <span className="rv-spinner" />}
                Save Draft (Estimate)
              </button>
              <button
                className="rv-btn rv-btn--primary"
                onClick={() => handleSubmit("Finalised")}
                disabled={saving}
              >
                {saving && <span className="rv-spinner" />}
                Finalise Bill (Complete)
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Invoice Detail Drawer ── */
function InvoiceDrawer({ invoice, items, products, inventoryItems = [], onClose, onEdit, onCancel, onPrint }) {
  const validProductIds = useMemo(() => new Set((products || []).map(p => Number(p.productID))), [products]);
  const lines = (items || [])
    .filter(i => Number(i.invoiceID) === Number(invoice.invoiceID))
    .filter(i => !i.productID || validProductIds.has(Number(i.productID)));
  const statusConf = STATUS_CONFIG[invoice.invoiceStatus] ?? STATUS_CONFIG.Draft;

  const inventoryItemMap = useMemo(() =>
    new Map((inventoryItems || []).map(i => [Number(i.inventoryItemID), i])),
    [inventoryItems]);

  const fmt = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const fmtAmt = v => `₹${parseFloat(v || 0).toFixed(2)}`;

  return (
    <div className="rv-dialog-backdrop" onClick={onClose}>
      <div className="rv-invitem-drawer" style={{ maxWidth: 680, width: "100%", maxHeight: "90vh" }} onClick={e => e.stopPropagation()}>
        <div className="rv-invitem-drawer-header" style={{ paddingRight: 48 }}>
          <div className="rv-invitem-drawer-avatar"><InvoiceIcon /></div>
          <div className="rv-invitem-drawer-title-wrap">
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
              <div className="rv-invitem-drawer-name" style={{ marginBottom: 0 }}>{invoice.invoiceNumber}</div>
              <span className={`rv-inv-status-badge rv-inv-status--${statusConf.color}`}>
                {invoice.invoiceStatus}
              </span>
            </div>
            <span className="rv-invitem-id-chip">{invoice.customerName} · {fmt(invoice.invoiceDate)}</span>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-invitem-drawer-body">

          {/* Customer info */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
            {[
              ["Customer", invoice.customerName || "—"],
              ["Phone", invoice.customerPhone || "—"],
              ["Address", invoice.customerAddress || "—"],
              ["GSTIN", invoice.customerGSTIN || "—"],
              ["Date", fmt(invoice.invoiceDate)],
            ].map(([lbl, val]) => (
              <div key={lbl} style={{ padding: "8px 10px", background: "rgba(185,167,146,.06)", borderRadius: 8 }}>
                <p style={{ fontSize: 10, color: "var(--taupe)", marginBottom: 2 }}>{lbl}</p>
                <p style={{ fontSize: 13, fontWeight: 600, color: "var(--dark)" }}>{val}</p>
              </div>
            ))}
          </div>

          {/* Line items */}
          {lines.length === 0 ? (
            <p style={{ fontSize: 13, color: "var(--taupe)", textAlign: "center", padding: "20px 0" }}>No line items.</p>
          ) : (
            <div className="rv-table-wrap">
              <table className="rv-table" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Wt (g)</th>
                    <th>Unit ₹</th>
                    <th>Making ₹</th>
                    <th>Tax%</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map(l => {
                    const prod = products.find(p => Number(p.productID) === Number(l.productID));
                    const invItem = l.inventoryItemID ? inventoryItemMap.get(Number(l.inventoryItemID)) : null;
                    return (
                      <tr key={l.invoiceItemID}>
                        <td>
                          <div>{prod?.productName ?? `#${l.productID}`}</div>
                          {l.inventoryItemID && (
                            <div style={{ fontSize: 10, color: "var(--taupe)", marginTop: 2 }}>
                              Item #{l.inventoryItemID}
                              {invItem && ` (Net: ${parseFloat(invItem.netWeight || 0).toFixed(3)}g)`}
                            </div>
                          )}
                        </td>
                        <td>{l.quantity}</td>
                        <td>{(l.billedWeight || invItem?.netWeight) ? `${parseFloat(l.billedWeight || invItem?.netWeight).toFixed(3)}g` : "—"}</td>
                        <td>{fmtAmt(l.unitPrice)}</td>
                        <td>{l.makingCharges && Number(l.makingCharges) > 0 ? fmtAmt(l.makingCharges) : "—"}</td>
                        <td>{l.taxPercentage}%</td>
                        <td><strong>{fmtAmt(l.totalPrice)}</strong></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Totals */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 12 }}>
            {[
              ["Subtotal", invoice.totalAmount],
              ["Tax", invoice.totalTaxAmount],
              ["Grand Total", invoice.grossAmount],
            ].map(([lbl, val]) => (
              <div key={lbl} style={{ textAlign: "center", padding: "8px", background: "rgba(185,167,146,.08)", borderRadius: 8 }}>
                <p style={{ fontSize: 10, color: "var(--taupe)", marginBottom: 2 }}>{lbl}</p>
                <p style={{ fontSize: 14, fontWeight: 700 }}>{fmtAmt(val)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rv-invitem-drawer-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose}>Close</button>
          <button
            className="rv-btn"
            style={{
              background: "linear-gradient(135deg, #b38743 0%, #8c6527 100%)",
              color: "#fff",
              border: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontWeight: 600,
              padding: "8px 14px",
              borderRadius: "8px",
              cursor: "pointer",
            }}
            onClick={() => onPrint && onPrint(invoice)}
          >
            <PrinterIcon /> Print Bill (A4)
          </button>
          {invoice.invoiceStatus === "Draft" && (
            <>
              <button className="rv-btn rv-btn--danger" onClick={() => onCancel(invoice)}>
                Cancel Draft
              </button>
              <button className="rv-btn rv-btn--primary" onClick={() => { onClose(); onEdit(invoice); }}>
                <EditIcon /> Edit Draft
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN INVOICE PAGE
══════════════════════════════════════ */
export default function InvoicePage() {
  const [invoices, setInvoices] = useState([]);
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [printInvoice, setPrintInvoice] = useState(null);
  const [toast, setToast] = useState(null);


  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  useResizableColumns(tableRef, tableReady, [50, 150, 110, 160, 120, 170, 110, 120, 100]);


  const [prices, setPrices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [leafCategories, setLeafCategories] = useState([]);
  const [taxConfigs, setTaxConfigs] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventorySummaries, setInventorySummaries] = useState([]);

  const fetchAll = async () => {
    try {
      setLoading(true); setTableReady(false);
      const [invData, itemData, prodData, priceData, catData, taxData, invItemData, invSummaryData] = await Promise.all([
        invoiceApi.getAll(),
        invoiceItemDtlApi.getAll(),
        productApi.getAll(),
        productPriceApi.getAll(),
        productCategoryApi.getAll(),
        taxConfigApi.getAll(),
        inventoryItemDtlApi.getAll().catch(() => []),
        inventorySummaryApi.getAll().catch(() => []),
      ]);
      setInvoices(Array.isArray(invData) ? invData : invData?.data ?? []);
      setItems(Array.isArray(itemData) ? itemData : itemData?.data ?? []);
      setProducts(Array.isArray(prodData) ? prodData : prodData?.data ?? []);
      setPrices(Array.isArray(priceData) ? priceData : priceData?.data ?? []);
      const rawCats = Array.isArray(catData) ? catData : catData?.data ?? [];
      const leafCats = rawCats.filter(c => c.parentCategoryID && c.parentCategoryID !== 0);
      setCategories(rawCats);
      setLeafCategories(leafCats);
      setTaxConfigs(Array.isArray(taxData) ? taxData : taxData?.data ?? []);
      setInventoryItems(Array.isArray(invItemData) ? invItemData : invItemData?.data ?? []);
      setInventorySummaries(Array.isArray(invSummaryData) ? invSummaryData : invSummaryData?.data ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load invoices.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  const showToast = (message, type = "success") => setToast({ message, type });

  /* ── Auto-generate invoice number ── */
  const generateInvoiceNumber = useCallback(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");

    const prefix = `INV-${year}-${month}-`;
    const existing = invoices
      .map(inv => inv.invoiceNumber ?? "")
      .filter(n => n.startsWith(prefix))
      .map(n => parseInt(n.replace(prefix, ""), 10))
      .filter(n => !isNaN(n));

    const next = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    return `${prefix}${String(next).padStart(3, "0")}`;
  }, [invoices]);

  /* ── Open new invoice with pre-scanned item (from BarcodeScanPopup) ── */
  const openWithScannedItem = useCallback((item) => {
    if (!item) return;
    if ((Number(item.quantity) || 0) <= 0) {
      showToast(`Cannot create invoice: Tag #${item.inventoryItemID} is already marked as sold.`, "error");
      return;
    }
    const invNum = generateInvoiceNumber();
    const billedWeight = item.netWeight != null
      ? String(item.netWeight)
      : item.grossWeight != null ? String(item.grossWeight) : "";

    const initialLine = {
      invoiceItemID: 0,
      productID: item.productID ? String(item.productID) : "",
      inventoryItemID: String(item.inventoryItemID),
      quantity: 1,
      billedWeight,
      unitPrice: "",
      metalRate: "",
      makingCharges: "",
      taxConfigID: 0,
      taxPercentage: "",
      lineAmount: 0,
      taxAmount: 0,
      totalPrice: 0,
    };

    setModal({
      data: {
        invoiceNumber: invNum,
        lines: [initialLine],
      },
    });
    showToast(`Started new invoice with scanned item #${item.inventoryItemID}`);
  }, [generateInvoiceNumber]);

  useEffect(() => {
    const stored = sessionStorage.getItem("reva_scanned_item_for_invoice");
    if (stored) {
      try {
        const item = JSON.parse(stored);
        sessionStorage.removeItem("reva_scanned_item_for_invoice");
        if (item?.inventoryItemID) {
          openWithScannedItem(item);
        }
      } catch (_) { }
    }

    const onScanInvoiceEvent = (e) => {
      if (e?.detail) openWithScannedItem(e.detail);
    };
    window.addEventListener("reva:open_invoice_with_item", onScanInvoiceEvent);
    return () => window.removeEventListener("reva:open_invoice_with_item", onScanInvoiceEvent);
  }, [openWithScannedItem]);

  /* ── Auto open New Invoice modal from Executive Short-Paths ── */
  useEffect(() => {
    if (!loading) {
      const autoModal = sessionStorage.getItem("reva_auto_modal");
      if (autoModal === "new-invoice") {
        sessionStorage.removeItem("reva_auto_modal");
        setModal({ data: { invoiceNumber: generateInvoiceNumber() } });
      }
    }
  }, [loading, generateInvoiceNumber]);

  useEffect(() => {
    const handleOpenModal = (e) => {
      if (e?.detail === "new-invoice") {
        sessionStorage.removeItem("reva_auto_modal");
        setModal({ data: { invoiceNumber: generateInvoiceNumber() } });
      }
    };
    window.addEventListener("reva:open_modal", handleOpenModal);
    return () => window.removeEventListener("reva:open_modal", handleOpenModal);
  }, [generateInvoiceNumber]);

  /* ── Save flow ── */
  const handleSave = async ({ header, lines }) => {
    setSaving(true);
    try {
      const now = new Date().toISOString();

      if (header.invoiceID) {
        // ── EDIT ──
        // 1. Fetch existing lines fresh to know which invoiceItemIDs already exist
        const existingItems = (items || []).filter(i => Number(i.invoiceID) === Number(header.invoiceID));

        // 2. Delete lines removed by the user
        const toDelete = existingItems.filter(
          existing => !lines.find(l => Number(l.invoiceItemID) === Number(existing.invoiceItemID || existing.invoiceItemDtlID))
        );
        for (const line of toDelete) {
          const idToDelete = line.invoiceItemID || line.invoiceItemDtlID;
          if (idToDelete) {
            await invoiceItemDtlApi.remove(idToDelete);
          }
        }

        const existingIDs = new Set(existingItems.map(i => Number(i.invoiceItemID || i.invoiceItemDtlID)));

        const toUpdate = lines.filter(l => l.invoiceItemID && existingIDs.has(Number(l.invoiceItemID)));
        const toCreate = lines.filter(l => !l.invoiceItemID || !existingIDs.has(Number(l.invoiceItemID)));

        // 3. Update existing and create new lines
        await Promise.all([
          ...toUpdate.map(l => {
            const isWeightBased = ["per gram", "per carat"].includes(
              (l.pricingType || products.find(p => Number(p.productID) === Number(l.productID))?.pricingType || "").toLowerCase()
            );
            return invoiceItemDtlApi.update({
              invoiceItemID: l.invoiceItemID,
              invoiceID: header.invoiceID,
              productID: l.productID,
              inventoryItemID: l.inventoryItemID ? Number(l.inventoryItemID) : null,
              quantity: l.quantity,
              billedWeight: isWeightBased && parseFloat(l.billedWeight) > 0 ? parseFloat(l.billedWeight) : null,
              unitPrice: l.unitPrice,
              metalRate: l.metalRate,
              makingCharges: l.makingCharges,
              lineAmount: l.lineAmount,
              taxConfigID: l.taxConfigID,
              taxPercentage: l.taxPercentage,
              taxAmount: l.taxAmount,
              totalPrice: l.totalPrice,
            });
          }),
          ...toCreate.map(l => {
            const isWeightBased = ["per gram", "per carat"].includes(
              (l.pricingType || products.find(p => Number(p.productID) === Number(l.productID))?.pricingType || "").toLowerCase()
            );
            return invoiceItemDtlApi.create({
              invoiceItemID: 0,
              invoiceID: header.invoiceID,
              productID: l.productID,
              inventoryItemID: l.inventoryItemID ? Number(l.inventoryItemID) : null,
              quantity: l.quantity,
              billedWeight: isWeightBased && parseFloat(l.billedWeight) > 0 ? parseFloat(l.billedWeight) : null,
              unitPrice: l.unitPrice,
              metalRate: l.metalRate,
              makingCharges: l.makingCharges,
              lineAmount: l.lineAmount,
              taxConfigID: l.taxConfigID,
              taxPercentage: l.taxPercentage,
              taxAmount: l.taxAmount,
              totalPrice: l.totalPrice,
            });
          }),
        ]);

        // 4. Update header with final status & totals
        await invoiceApi.update({
          invoiceID: header.invoiceID,
          invoiceNumber: header.invoiceNumber,
          invoiceDate: header.invoiceDate,
          customerName: header.customerName,
          customerPhone: header.customerPhone,
          customerAddress: header.customerAddress || "",
          customerGSTIN: header.customerGSTIN,
          totalAmount: header.totalAmount,
          totalTaxAmount: header.totalTaxAmount,
          grossAmount: header.grossAmount,
          invoiceStatus: header.invoiceStatus,
          lastUPDTTM: now,
          lastUPDID: 0,
        });

        showToast(
          header.invoiceStatus === "Finalised"
            ? "Invoice finalised successfully."
            : "Invoice updated."
        );

      } else {
        // ── CREATE ──
        let createdInvoiceID = null;
        try {
          // 1. POST header with invoiceStatus: "Draft" → get invoiceID
          const created = await invoiceApi.create({
            invoiceID: 0,
            invoiceNumber: header.invoiceNumber,
            invoiceDate: header.invoiceDate,
            customerName: header.customerName,
            customerPhone: header.customerPhone,
            customerAddress: header.customerAddress || "",
            customerGSTIN: header.customerGSTIN,
            totalAmount: header.totalAmount,
            totalTaxAmount: header.totalTaxAmount,
            grossAmount: header.grossAmount,
            invoiceStatus: "Draft",
            lastUPDTTM: now,
            lastUPDID: 0,
          });
          createdInvoiceID = created?.invoiceID ?? created?.data?.invoiceID;
          if (!createdInvoiceID) throw new Error("No invoiceID returned from server.");

          // 2. POST each line with the new invoiceID
          await Promise.all(lines.map(l => {
            const isWeightBased = ["per gram", "per carat"].includes(
              (l.pricingType || products.find(p => Number(p.productID) === Number(l.productID))?.pricingType || "").toLowerCase()
            );
            return invoiceItemDtlApi.create({
              invoiceItemID: 0,
              invoiceID: createdInvoiceID,
              productID: l.productID,
              inventoryItemID: l.inventoryItemID ? Number(l.inventoryItemID) : null,
              quantity: l.quantity,
              billedWeight: isWeightBased && parseFloat(l.billedWeight) > 0 ? parseFloat(l.billedWeight) : null,
              unitPrice: l.unitPrice,
              metalRate: l.metalRate,
              makingCharges: l.makingCharges,
              lineAmount: l.lineAmount,
              taxConfigID: l.taxConfigID,
              taxPercentage: l.taxPercentage,
              taxAmount: l.taxAmount,
              totalPrice: l.totalPrice,
            });
          }));

          // 3. If user clicked "Finalise Invoice", update invoiceStatus to "Finalised"
          if (header.invoiceStatus === "Finalised") {
            await invoiceApi.update({
              invoiceID: createdInvoiceID,
              invoiceNumber: header.invoiceNumber,
              invoiceDate: header.invoiceDate,
              customerName: header.customerName,
              customerPhone: header.customerPhone,
              customerAddress: header.customerAddress || "",
              customerGSTIN: header.customerGSTIN,
              totalAmount: header.totalAmount,
              totalTaxAmount: header.totalTaxAmount,
              grossAmount: header.grossAmount,
              invoiceStatus: "Finalised",
              lastUPDTTM: new Date().toISOString(),
              lastUPDID: 0,
            });
          }

          showToast(
            header.invoiceStatus === "Finalised"
              ? "Invoice finalised successfully."
              : "Invoice saved as draft."
          );
        } catch (createErr) {
          // Solution 3: If lines creation or finalisation failed on a newly created invoice,
          // silently rollback by deleting any inserted lines and the draft invoice header
          if (createdInvoiceID) {
            try {
              const freshItems = await invoiceItemDtlApi.getAll().catch(() => []);
              const linesToDelete = (freshItems || []).filter(i => Number(i.invoiceID) === Number(createdInvoiceID));
              for (const line of linesToDelete) {
                const itemId = line.invoiceItemID || line.invoiceItemDtlID;
                if (itemId) await invoiceItemDtlApi.remove(itemId).catch(() => {});
              }
              await invoiceApi.remove(createdInvoiceID).catch(() => {});
            } catch (_) {}
          }
          throw createErr;
        }
      }

      setModal(null);
      emitDataMutation("invoice");
      fetchAll();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Cancel invoice ── */
  const handleCancelInvoice = (invoice) => {
    setConfirm({
      type: "cancel",
      invoice,
      title: "Cancel Invoice?",
      message: `Invoice ${invoice.invoiceNumber} will be marked as Cancelled. Stock will NOT be affected. This cannot be undone.`,
      confirmLabel: "Yes, Cancel Invoice",
      confirmClass: "rv-btn--danger",
    });
  };

  /* ── Delete invoice (Draft only) ── */
  const handleDeleteInvoice = (invoice) => {
    setConfirm({
      type: "delete",
      invoice,
      title: "Delete Invoice?",
      message: `Invoice ${invoice.invoiceNumber} will be permanently deleted. This action cannot be undone.`,
      confirmLabel: "Delete",
      confirmClass: "rv-btn--danger",
    });
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    try {
      const now = new Date().toISOString();
      if (confirm.type === "delete") {
        const invoiceID = confirm.invoice.invoiceID;
        const linesToDelete = (items || []).filter(i => Number(i.invoiceID) === Number(invoiceID));
        for (const line of linesToDelete) {
          const itemId = line.invoiceItemID || line.invoiceItemDtlID;
          if (itemId) {
            await invoiceItemDtlApi.remove(itemId);
          }
        }
        await invoiceApi.remove(invoiceID);
        showToast("Invoice deleted.");
      } else if (confirm.type === "cancel") {
        await invoiceApi.update({
          invoiceID: confirm.invoice.invoiceID,
          invoiceNumber: confirm.invoice.invoiceNumber,
          invoiceDate: confirm.invoice.invoiceDate,
          customerName: confirm.invoice.customerName,
          customerPhone: confirm.invoice.customerPhone,
          customerGSTIN: confirm.invoice.customerGSTIN,
          totalAmount: confirm.invoice.totalAmount,
          totalTaxAmount: confirm.invoice.totalTaxAmount,
          grossAmount: confirm.invoice.grossAmount,
          invoiceStatus: "Cancelled",
          lastUPDTTM: now,
          lastUPDID: 0,
        });
        showToast("Invoice cancelled.");
      }
      setConfirm(null);
      setDrawer(null);
      emitDataMutation("invoice");
      fetchAll();
    } catch (err) {
      showToast(err.message || "Action failed.", "error");
    }
  };

  /* ── Product mapping for invoice table ── */
  const productMap = useMemo(() =>
    new Map((products || []).map(p => [Number(p.productID), (p.productName || "").trim()])),
    [products]
  );

  const invoiceProductsMap = useMemo(() => {
    const map = new Map();
    const validProductIds = new Set((products || []).map(p => Number(p.productID)));
    (items || []).forEach(item => {
      const invId = Number(item.invoiceID);
      const prodId = Number(item.productID);
      if (!invId || !prodId || !validProductIds.has(prodId)) return;
      const pName = productMap.get(prodId) || `#${prodId}`;
      if (!pName) return;
      if (!map.has(invId)) {
        map.set(invId, []);
      }
      const list = map.get(invId);
      if (!list.includes(pName)) {
        list.push(pName);
      }
    });
    return map;
  }, [items, products, productMap]);

  /* ── Filtered list ── */
  const filtered = invoices.filter(inv => {
    const q = search.toLowerCase();
    const invProdNames = invoiceProductsMap.get(Number(inv.invoiceID)) || [];
    const matchSearch =
      !q ||
      inv.invoiceNumber?.toLowerCase().includes(q) ||
      inv.customerName?.toLowerCase().includes(q) ||
      inv.customerPhone?.toLowerCase().includes(q) ||
      String(inv.invoiceID).includes(q) ||
      invProdNames.some(name => name.toLowerCase().includes(q));
    const matchStatus = filterStatus === "all" || inv.invoiceStatus === filterStatus;
    return matchSearch && matchStatus;
  });

  const { paged, page, totalPages, pageSize, setPage, setPageSize, pageStart, pageEnd, totalItems } =
    usePagination(filtered, 10);

  const fmt = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const fmtAmt = v => `₹${parseFloat(v || 0).toFixed(2)}`;

  /* ── Open edit modal with existing line items ── */
  const openEdit = (invoice) => {
    const validProductIds = new Set((products || []).map(p => Number(p.productID)));
    const invoiceLines = (items || [])
      .filter(i => Number(i.invoiceID) === Number(invoice.invoiceID))
      .filter(i => i.productID && validProductIds.has(Number(i.productID)))
      .sort((a, b) => Number(a.invoiceItemDtlID || a.invoiceItemID || a.lineNumber || 0) - Number(b.invoiceItemDtlID || b.invoiceItemID || b.lineNumber || 0))
      .map(l => ({
        ...l,
        productID: Number(l.productID),
        makingCharges: l.makingCharges != null ? String(l.makingCharges) : "",
      }));
    setModal({ data: { ...invoice, lines: invoiceLines } });
  };

  /* ── Auto open Invoice for review/edit from Dashboard Review action ── */
  useEffect(() => {
    if (!loading && invoices?.length > 0) {
      const reviewInvId = sessionStorage.getItem("reva_review_invoice_id");
      if (reviewInvId) {
        sessionStorage.removeItem("reva_review_invoice_id");
        const target = invoices.find(inv => String(inv.invoiceID) === String(reviewInvId));
        if (target) {
          openEdit(target);
        }
      }
    }
  }, [loading, invoices, items, products]);


  return (
    <div className="rv-inv-page">

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {modal && (
        <InvoiceModal
          initial={modal.data}
          products={products}
          allItems={items}
          inventoryItems={inventoryItems}
          inventorySummaries={inventorySummaries}
          prices={prices}
          categories={categories}
          taxConfigs={taxConfigs}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}

      {drawer && (
        <InvoiceDrawer
          invoice={drawer}
          items={items}
          products={products}
          inventoryItems={inventoryItems}
          onClose={() => setDrawer(null)}
          onEdit={openEdit}
          onCancel={handleCancelInvoice}
          onPrint={(inv) => setPrintInvoice(inv)}
        />
      )}

      {printInvoice && (
        <PrintInvoiceModal
          invoice={printInvoice}
          lines={items.filter((i) => Number(i.invoiceID) === Number(printInvoice.invoiceID))}
          products={products}
          categories={categories}
          inventoryItems={inventoryItems}
          onClose={() => setPrintInvoice(null)}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          confirmClass={confirm.confirmClass}
          onConfirm={handleConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* ── Topbar ── */}
      <div className="rv-inv-topbar">
        <div className="rv-inv-topbar-left">
          <h2 className="rv-inv-heading">Customer Bills &amp; Invoices</h2>
          <span className="rv-inv-count">{invoices.length} {invoices.length === 1 ? "bill" : "bills"}</span>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: { invoiceNumber: generateInvoiceNumber() } })}>
          <PlusIcon /> Create New Bill
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="rv-inv-filters">
        <div className="rv-inv-search">
          <SearchIcon />
          <input
            placeholder="Search by bill number, customer name, phone or product…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="rv-inv-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>
        <div className="rv-filter-tabs">
          {[
            { key: "all", label: "All Bills" },
            { key: "Draft", label: "Saved Estimates (Draft)" },
            { key: "Finalised", label: "Completed Bills" },
            { key: "Cancelled", label: "Cancelled" },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`rv-filter-tab${filterStatus === key ? " rv-filter-tab--active" : ""}`}
              onClick={() => { setFilterStatus(key); setPage(1); }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table card ── */}
      <div className="rv-card rv-inv-card">
        {loading ? (
          <div className="rv-inv-empty"><div className="rv-loader" /><p>Loading bills…</p></div>
        ) : filtered.length === 0 ? (
          <div className="rv-inv-empty">
            <div className="rv-empty-icon"><InvoiceIcon /></div>
            <p className="rv-empty-title">No bills found</p>
            <p className="rv-empty-sub">
              {search ? "Try a different search." : "Create your first customer bill to get started."}
            </p>
            {!search && (
              <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: { invoiceNumber: generateInvoiceNumber() } })}>
                <PlusIcon /> Create New Bill
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="rv-desktop-table-view">
              <div className="rv-table-wrap rv-inv-table-wrap">
                <table className="rv-table rv-inv-table" ref={tableRef}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Bill No.</th>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Phone</th>
                      <th>Items Purchased</th>
                      <th>Status</th>
                      <th>Total Amount (₹)</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((inv, i) => {
                      const statusConf = STATUS_CONFIG[inv.invoiceStatus] ?? STATUS_CONFIG.Draft;
                      const isDraft = inv.invoiceStatus === "Draft";
                      const prodNames = invoiceProductsMap.get(Number(inv.invoiceID)) || [];
                      return (
                        <tr
                          key={inv.invoiceID}
                          className="rv-txn-row"
                          style={{ cursor: "pointer" }}
                          onClick={() => setDrawer(inv)}
                        >
                          <td>{pageStart + i}</td>
                          <td><span className="rv-txn-id-badge">{inv.invoiceNumber}</span></td>
                          <td>{fmt(inv.invoiceDate)}</td>
                          <td>{inv.customerName}</td>
                          <td>{inv.customerPhone || "—"}</td>
                          <td>
                            {prodNames.length === 0 ? (
                              <span style={{ color: "var(--taupe)" }}>—</span>
                            ) : prodNames.length === 1 ? (
                              <span style={{ fontWeight: 500 }}>{prodNames[0]}</span>
                            ) : (
                              <span title={prodNames.join(", ")} style={{ fontWeight: 500 }}>
                                {prodNames.slice(0, 2).join(", ")}
                                {prodNames.length > 2 && (
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      background: "rgba(185,167,146,.15)",
                                      color: "var(--taupe-dark, #635343)",
                                      padding: "1px 6px",
                                      borderRadius: "10px",
                                      fontWeight: 600,
                                      marginLeft: "4px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    +{prodNames.length - 2} more
                                  </span>
                                )}
                              </span>
                            )}
                          </td>
                          <td>
                            <span className={`rv-inv-status-badge rv-inv-status--${statusConf.color}`}>
                              {inv.invoiceStatus}
                            </span>
                          </td>
                          <td><strong>{fmtAmt(inv.grossAmount)}</strong></td>
                          <td onClick={e => e.stopPropagation()}>
                            <div className="rv-actions">
                              <button
                                className="rv-action-btn"
                                title="Print Bill (A4)"
                                onClick={() => setPrintInvoice(inv)}
                              ><PrinterIcon /></button>
                              {isDraft && (
                                <button
                                  className="rv-action-btn rv-action-btn--edit"
                                  title="Edit Draft"
                                  onClick={() => openEdit(inv)}
                                ><EditIcon /></button>
                              )}
                              {isDraft && (
                                <button
                                  className="rv-action-btn rv-action-btn--del"
                                  title="Delete Draft"
                                  onClick={() => handleDeleteInvoice(inv)}
                                ><TrashIcon /></button>
                              )}
                              {!isDraft && (
                                <button
                                  className="rv-action-btn rv-action-btn--edit"
                                  title="View Bill Details"
                                  onClick={() => setDrawer(inv)}
                                ><EyeIcon /></button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Cards View */}
            <div className="rv-mobile-cards-view">
              <RvDataCardList
                loading={loading}
                items={paged}
                emptyIcon={<InvoiceIcon />}
                emptyTitle="No invoices found"
                emptySubtitle={search ? "Try a different search term." : "Create your first customer invoice to get started."}
                emptyAction={
                  !search && (
                    <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: { invoiceNumber: generateInvoiceNumber() } })}>
                      <PlusIcon /> New Invoice
                    </button>
                  )
                }
                renderCard={(inv, i) => {
                  const statusConf = STATUS_CONFIG[inv.invoiceStatus] ?? STATUS_CONFIG.Draft;
                  const isDraft = inv.invoiceStatus === "Draft";
                  return (
                    <RvDataCard
                      key={inv.invoiceID}
                      index={pageStart + i}
                      title={inv.customerName || "Customer"}
                      subtitle={inv.invoiceNumber}
                      badge={
                        <span className={`rv-inv-status-badge rv-inv-status--${statusConf.color}`}>
                          {inv.invoiceStatus}
                        </span>
                      }
                      fields={[
                        { label: "Invoice Date", value: fmt(inv.invoiceDate) },
                        { label: "Product Name", value: (invoiceProductsMap.get(Number(inv.invoiceID)) || []).join(", ") || "—" },
                        { label: "Gross Amount", value: <strong style={{ color: "var(--dark)", fontSize: 13.5 }}>{fmtAmt(inv.grossAmount)}</strong> },
                        { label: "Customer Phone", value: inv.customerPhone || "—" },
                        { label: "Items Count", value: inv.itemCount ? `${inv.itemCount} items` : "—" },
                      ]}
                      onClick={() => setDrawer(inv)}
                      actions={
                        <div className="rv-actions" onClick={e => e.stopPropagation()}>
                          <button
                            className="rv-action-btn"
                            title="Print A4 Invoice"
                            onClick={() => setPrintInvoice(inv)}
                          ><PrinterIcon /></button>
                          {isDraft && (
                            <button
                              className="rv-action-btn rv-action-btn--edit"
                              title="Edit"
                              onClick={() => openEdit(inv)}
                            ><EditIcon /></button>
                          )}
                          {isDraft && (
                            <button
                              className="rv-action-btn rv-action-btn--del"
                              title="Delete"
                              onClick={() => handleDeleteInvoice(inv)}
                            ><TrashIcon /></button>
                          )}
                          {!isDraft && (
                            <button
                              className="rv-action-btn rv-action-btn--edit"
                              title="View"
                              onClick={() => setDrawer(inv)}
                            ><EyeIcon /></button>
                          )}
                        </div>
                      }
                    />
                  );
                }}
              />
            </div>

            {/* Shared Pagination */}
            <Pagination
              page={page} totalPages={totalPages} pageSize={pageSize}
              setPage={setPage} setPageSize={setPageSize}
              pageStart={pageStart} pageEnd={pageEnd} totalItems={totalItems}
            />
          </>
        )}
      </div>

      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint rv-desktop-table-view"><GripIcon /> Drag column borders to resize · Click a row to view details</p>
      )}
    </div>
  );
}

/* ── Icons ── */
function PlusIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function SearchIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>; }
function EditIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>; }
function TrashIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>; }
function CloseIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>; }
function CheckIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>; }
function AlertIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>; }
function GripIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ width: 13, height: 13 }}><circle cx="9" cy="5" r="1" fill="currentColor" /><circle cx="9" cy="12" r="1" fill="currentColor" /><circle cx="9" cy="19" r="1" fill="currentColor" /><circle cx="15" cy="5" r="1" fill="currentColor" /><circle cx="15" cy="12" r="1" fill="currentColor" /><circle cx="15" cy="19" r="1" fill="currentColor" /></svg>; }
function InvoiceIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>; }
function EyeIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>; }
function PrinterIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="16" height="16"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>; }
function BarcodeIcon() { return <i className="bi bi-upc-scan" style={{ fontSize: 15, lineHeight: 1 }} />; }